import { useEffect, useRef, type ReactNode } from 'react';
import { S, t, type Leaf } from '../i18n/strings';
import { cx } from '../lib/cx';
import { useActions, useSteve } from '../state/context';
import type { Skin } from '../state/types';
import styles from './EasterEgg.module.css';

const BLOBS = [styles.blob1, styles.blob2, styles.blob3, styles.blob4];

/**
 * L'habillage de l'overlay, skin par skin.
 *
 * `string | undefined` : les modules CSS sont typés indice par indice, et un
 * nom de classe absent vaut `undefined`. `cx` l'accepte, l'overlay retombe
 * alors sur les valeurs de repli — c'est exactement ce qu'on veut d'un
 * dixième skin ajouté sans variante.
 */
const LOOKS: Record<Skin, string | undefined> = {
  aqua: styles.aqua,
  y2k: styles.y2k,
  saas: styles.saas,
  paint: styles.paint,
  pixel: styles.pixel,
  matrix: styles.matrix,
  xp: styles.xp,
  skyblog: styles.skyblog,
  threed: styles.threed,
  wiki: styles.wiki,
  search: styles.search,
};

/** Les trois lignes de l'overlay, skin par skin. */
interface EggText {
  readonly title: Leaf;
  readonly line: Leaf;
  readonly close: Leaf;
}

/**
 * Overlay de l'easter egg — **il prend le vocabulaire du skin où on l'a trouvé**.
 *
 * Un seul overlay pour neuf interfaces, c'était le seul endroit du site où le
 * costume tombait : on venait de passer dix minutes dans un Skyblog de 2005 ou
 * dans un terminal vert, et la récompense arrivait en bleu d'aquarium. Le
 * mécanisme ne change pas — même mot, même fermeture partout — mais la fenêtre,
 * la police, les couleurs et les trois lignes sont celles du skin courant.
 *
 * Chaque variante n'ajoute qu'**une classe** sur l'overlay : la mise en page
 * reste commune, ce sont les couleurs, la police et le décor qui changent. Une
 * variante par composant aurait multiplié par neuf la surface d'un morceau
 * d'interface qui, au fond, affiche trois lignes de texte.
 */
export function EasterEgg(): ReactNode {
  const { egg, lang, skin } = useSteve();
  const { closeEgg } = useActions();
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!egg) return;
    ref.current?.focus();
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') closeEgg();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [egg, closeEgg]);

  if (!egg) return null;

  // Les neuf variantes existent toutes ; le repli générique reste néanmoins la
  // valeur par défaut, pour qu'un dixième skin ajouté sans texte propre affiche
  // quelque chose plutôt que rien.
  const text: EggText = S.egg.skins[skin] ?? S.egg;

  return (
    <button
      ref={ref}
      type="button"
      className={cx(styles.overlay, LOOKS[skin])}
      onClick={closeEgg}
      data-egg-skin={skin}
    >
      <span className={styles.panel}>
        <span className={styles.title}>{t(text.title, lang)}</span>
        <span className={styles.line}>{t(text.line, lang)}</span>
        <span className={styles.close}>{t(text.close, lang)}</span>
      </span>
      {BLOBS.map((blob, index) => (
        <span key={index} className={cx(styles.blob, blob)} aria-hidden="true">
          <span />
        </span>
      ))}
    </button>
  );
}
